import { Grid, Typography } from "@mui/material";
import {
  ContactNumberInput,
  EmailInput,
  FirstNameInput,
  LastNameInput,
  NameInput,
  OptionalText,
  useGetSessionState,
} from "raci-react-library";
import React from "react";
import { FormProvider } from "react-hook-form";
import { CallToActionButton } from "../../shared/components/CallToActionButon";
import DuplicatePolicyDialog from "../../shared/components/DuplicatePolicyDialog";
import MailingAddress, { contactMatchedLabel } from "../../shared/components/MailingAddress";
import NeedMoreInformationCardNotification from "../../shared/components/NeedMoreInformationCardNotification";
import { PrivacyPolicy } from "../../shared/components/PrivacyPolicy";
import TitleGender from "../../shared/components/TitleGender";
import { CONFIRM_TEXT, NEXT_TEXT } from "../../shared/constants";
import useFormotiv from "../../shared/hooks/useFormotiv";
import { QuoteRoutes } from "../../shared/routing/routes.config";
import { Values as YourQuoteValues } from "../YourQuote/types";
import { FormProps } from "./types";

export const Form: React.FC<FormProps> = ({
  form,
  data: {
    hasMemberMatched,
    isMultiMatch,
    isLoading,
    isTitleAndGenderRequired,
    showMoreInfoCard,
    showDuplicatePolicyDialog,
  },
  onSubmit,
  onCloseDuplicatePolicyDialog,
}) => {
  const isSingleMatch = hasMemberMatched && !isMultiMatch;
  const mailingAddressLabel = isSingleMatch ? contactMatchedLabel : null;
  const buttonText = isSingleMatch ? CONFIRM_TEXT : NEXT_TEXT;
  const onSubmitWrapper = useFormotiv(onSubmit);

  const { quote } = useGetSessionState<YourQuoteValues>(QuoteRoutes.YourQuote);
  const quoteNumber = quote?.quoteNumber;

  const moreInfoCard = showMoreInfoCard ? (
    <Grid item xs={12}>
      <NeedMoreInformationCardNotification quoteNumber={quoteNumber} />
    </Grid>
  ) : null;

  return (
    <>
      <FormProvider {...form}>
        <form action="#" onSubmit={form.handleSubmit(onSubmitWrapper)} noValidate>
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <Typography variant="h2" id="header" data-testid="header">
                Tell us more about you
              </Typography>
            </Grid>
            <TitleGender show={isTitleAndGenderRequired && !isLoading} />
            {!hasMemberMatched && (
              <Grid item xs={12}>
                <FirstNameInput />
              </Grid>
            )}
            {(isMultiMatch || !hasMemberMatched) && (
              <Grid item xs={12}>
                <NameInput
                  inputId="middleName"
                  formControllerName="middleName"
                  fieldName="middle name"
                  fieldLabel="Middle name"
                  placeholderText="e.g. James"
                  autoCompleteCategory="middle-name"
                  maxLength={50}
                  optional={true}
                  optionalText={OptionalText.Optional}
                  required={false}
                />
              </Grid>
            )}
            {(isMultiMatch || !hasMemberMatched) && (
              <Grid item xs={12}>
                <LastNameInput />
              </Grid>
            )}
            {!hasMemberMatched && (
              <Grid item xs={12}>
                <ContactNumberInput />
              </Grid>
            )}
            {!hasMemberMatched && (
              <Grid item xs={12}>
                <EmailInput />
              </Grid>
            )}
            <Grid item xs={12}>
              <MailingAddress label={mailingAddressLabel} />
            </Grid>
            {!hasMemberMatched && (
              <Grid item xs={12}>
                <PrivacyPolicy />
              </Grid>
            )}
            {moreInfoCard}
            <Grid item xs={12}>
              <CallToActionButton
                type="submit"
                data-testid="submit"
                color="primary"
                variant="contained"
                disabled={showMoreInfoCard || showDuplicatePolicyDialog}
                fullWidth
              >
                {buttonText}
              </CallToActionButton>
            </Grid>
          </Grid>
        </form>
      </FormProvider>
      <DuplicatePolicyDialog show={showDuplicatePolicyDialog ?? false} onClose={onCloseDuplicatePolicyDialog} />
    </>
  );
};

export default Form;
